import type { FC } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import TeamName from 'app/components/TeamName';
import BlockTemplate from 'app/components/Blocks/BlockTemplate';

import MapStats from './MapStats';
import style from './style.module.scss';
import { getTeamStatsType, hasTeamData } from './utils';
import Dota2TeamStats from './Dota2TeamStats';
import LolTeamStats from './LolTeamStats';

interface Props {
  data: TeamsStatsBlockMatch;
}

const TeamsStatsBlock: FC<Props> = ({ data }) => {
  const { t } = useTranslation();

  const [expandedId, setExpandedId] = useState<number | null>(data.maps_winrate?.[0]?.id || null);

  useEffect(() => {
    setExpandedId(data.maps_winrate?.[0]?.id || null);
  }, [data.maps_winrate]);

  const toggleExpanded = (mapId: number) => {
    if (mapId === expandedId) {
      setExpandedId(null);
    } else {
      setExpandedId(mapId);
    }
  };

  const teamStatsType = useMemo(() => getTeamStatsType(data), []);
  const hasData = hasTeamData(data, teamStatsType);

  const opponent1 = data.opponents.at(0);
  const opponent2 = data.opponents.at(1);

  return (
    <BlockTemplate
      title={t(`blocks_teams-stats_title_${teamStatsType ?? 'team'}`)}
      subtitle={teamStatsType === 'team' ? t('blocks_teams-stats_subtitle_team') : undefined}
      containerClassname={style.teamStatsBlockContent}
      withPeriodSelector
      hasData={hasData}
    >
      {(() => {
        switch (data.videogame_slug) {
          case 'cs-go':
          case 'dota-2':
            return (
              <TeamName.Container className={style.teamsNames}>
                <TeamName team={opponent1} logoStyle={style.teamNameLogoSize} />
                <TeamName
                  team={opponent2}
                  logoStyle={style.teamNameLogoSize}
                  orientation="reversed"
                />
              </TeamName.Container>
            );
          default:
            return null;
        }
      })()}

      {(() => {
        switch (data.videogame_slug) {
          case 'cs-go':
            return data.maps_winrate!.map((map, index) => (
              <MapStats
                key={`${map.id}-${index}`}
                data={map}
                videogame={data.videogame_slug}
                selectCallback={() => toggleExpanded(map.id)}
                expanded={expandedId === map.id}
                opponent1Id={opponent1?.id}
                opponent2Id={opponent2?.id}
              />
            ));
          case 'dota-2':
            return <Dota2TeamStats data={data} />;
          case 'league-of-legends':
            return <LolTeamStats data={data} />;
          default:
            return null;
        }
      })()}
    </BlockTemplate>
  );
};

TeamsStatsBlock.displayName = 'TeamsStatsBlock';

export default TeamsStatsBlock;
